import React, { useEffect, useState, useRef } from 'react';
import styles from './FullscreenImageModal.module.css';
import { Image } from './ImagePreviewModal';

interface FullscreenImageModalProps {
  image: Image;
  isOpen: boolean;
  onClose: () => void;
}

const FullscreenImageModal: React.FC<FullscreenImageModalProps> = ({
  image,
  isOpen,
  onClose,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Reset zoom and position when image changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [image]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(prevScale => {
      const newScale = Math.min(Math.max(prevScale + delta, 0.5), 5);
      // Reset position if zooming to 1 or less
      if (newScale <= 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  };

  // Handle mouse drag
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle touch zoom (pinch) and panning
  const touchStartRef = useRef<{ dist: number; scale: number; touch: { x: number; y: number } } | null>(null);

  const getTouchDistance = (touches: React.TouchList): number => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Two-finger pinch zoom
      const dist = getTouchDistance(e.touches);
      touchStartRef.current = { dist, scale, touch: { x: 0, y: 0 } };
    } else if (e.touches.length === 1 && scale > 1) {
      // Single-finger pan when zoomed
      const touch = e.touches[0];
      touchStartRef.current = {
        dist: 0,
        scale,
        touch: {
          x: touch.clientX - position.x,
          y: touch.clientY - position.y,
        }
      };
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    if (e.touches.length === 2) {
      // Two-finger pinch zoom
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      const scaleDelta = dist / touchStartRef.current.dist;
      const newScale = Math.min(Math.max(touchStartRef.current.scale * scaleDelta, 0.5), 5);
      setScale(newScale);
      if (newScale <= 1) {
        setPosition({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && scale > 1 && isDragging) {
      // Single-finger pan when zoomed
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - touchStartRef.current.touch.x,
        y: touch.clientY - touchStartRef.current.touch.y,
      });
    }
  };

  const handleTouchEnd = () => {
    touchStartRef.current = null;
    setIsDragging(false);
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Select appropriate image size
  const getOptimalImageUrl = (): string => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 1024) {
      return image.mediumPath;
    } else {
      return image.originalPath;
    }
  };

  const imageUrl = getOptimalImageUrl();

  return (
    <div
      className={styles.modalOverlay}
      onClick={handleBackdropClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <button
        className={styles.closeButton}
        onClick={onClose}
        aria-label="Close fullscreen image"
      >
        ×
      </button>

      <div
        ref={imageContainerRef}
        className={styles.imageContainer}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        <img
          src={imageUrl}
          alt={image.name}
          className={styles.image}
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
          }}
          draggable={false}
        />
      </div>

      {scale !== 1 && (
        <div className={styles.zoomIndicator}>
          {Math.round(scale * 100)}%
        </div>
      )}
    </div>
  );
};

export default FullscreenImageModal;
